import type { SecurityProperties } from '@unifyapps/network/utils/securityHeaders';
import type { Entity } from '../page/entity';
import type { DeviceDetails } from '../deviceVariant';
import type { Event } from '../event';
import type { BlockThemeEnum, StyleObject } from '../styleObject';
import type { TypographyVariants, TypographyWeights } from '../typography';
import type { ThemeComponentsType } from './types';

export enum NavigationResolution {
  XL = 'XL',
  LG = 'LG',
  MD = 'MD',
  SM = 'SM',
}

export enum ApplicationType {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
  COPILOT = 'COPILOT',
}

export type Metadata = {
  description?: string;
  logo: {
    favicon?: string;
    small: string;
    large: string;
  };
  icon?: string;
  locale?: string;
  supportedLocales?: string[];
  advanced?: {
    location?: {
      enable?: boolean;
      liveTracking?: boolean;
    };
  };
  _counter?: {
    _pageCount?: number;
    _moduleCount?: number;
  };
};

export type InterfacePermissions = Record<string, string[]>;

export type PagePermissions = Record<string, InterfacePermissions>;

interface BaseNavItem {
  id: string;
  name: string;
  permissions?: InterfacePermissions;
  breakpointVisibility?: NavigationResolution[];
}

export interface UrlNavItem extends BaseNavItem {
  type: 'URL';
  openInNewTab?: boolean;
  url: string;
  icon: string;
}

export interface PageNavItem extends BaseNavItem {
  type: 'PAGE';
  pageId: string;
  slug: string;
  pageInputs?: Record<string, unknown>;
  icon: string;
}

export interface GroupNavItem extends BaseNavItem {
  type: 'GROUP';
  items: NavItem[];
  icon: string;
}

export interface UserMenuNavItem extends BaseNavItem {
  type: 'USER_MENU';
  profilePage?: { pageId?: string; pageInputs?: Record<string, unknown> };
}

export interface ActionNavItem extends BaseNavItem {
  type: 'ACTION';
  action?: 'showNotifications'; // can add more actions as needed
  onClick: (args: { onOpen: () => void; onClose: () => void }) => void;
  icon: string;
}

export type NavItem = UrlNavItem | PageNavItem | GroupNavItem | UserMenuNavItem | ActionNavItem;

export type NavItems = NavItem[];

export type NavItemsMap = Map<string, NavItem>;

export enum NavigationPosition {
  Start = 'start',
  End = 'end',
  Bottom = 'bottom',
  Top = 'top',
}

export enum NavigatorType {
  None = 'None',
  DrawerNavigator = 'DrawerNavigator',
  BottomTabsNavigator = 'BottomTabsNavigator',
}

export enum NavigationSelectionVariant {
  Stroke = 'stroke',
  Solid = 'solid',
}

export enum NavigationBarType {
  Fixed = 'fixed',
  Floating = 'floating',
}

export enum NavigationItemLabelVariant {
  Expandable = 'expandable',
  Upfront = 'upfront',
  Tooltip = 'tooltip',
  None = 'none',
}

export enum NavigationItemDirection {
  Column = 'column',
  Row = 'row',
}

export enum NavigationItemAlignment {
  Start = 'start',
  Center = 'center',
}

export type NavigationItemText = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
  variant: TypographyVariants;
  weight: TypographyWeights;
  fontFamily: string;
};

export enum NavigationItemIconSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export type NavigationItemIcon = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
  size: NavigationItemIconSize;
};

export type NavigationItemBackground = {
  colors: {
    default: string;
    hover: string;
    active: string;
  };
};

// XS is 52px and SM is 56px in Navbar Width
export enum ExpandableNavigationVariant {
  XS = 'xs',
  SM = 'sm',
}

export type AppearanceNavItem = {
  id: string;
  items?: AppearanceNavItem[];
  hiddenInOverflow: boolean | undefined;
  visible?: boolean;
};

export type AppearanceNavItems = AppearanceNavItem[];

type BaseNavigationBarAppearance = {
  navigatorType?: NavigatorType;
  overflowDetails: {
    icon: string;
    label: string;
  };
};

export type NavigationItemStyles = Pick<StyleObject, 'padding' | 'gap' | 'borderRadius'>;

export type NavigationItem = {
  selectionVariant: NavigationSelectionVariant;
  labelVariant: NavigationItemLabelVariant;
  direction?: NavigationItemDirection;
  alignment?: NavigationItemAlignment;
  alignmentColumn?: NavigationItemAlignment;
  text?: NavigationItemText;
  icon?: NavigationItemIcon;
  background?: NavigationItemBackground;
  styles?: NavigationItemStyles;
};

export type NavigationAppearance = {
  primary: AppearanceNavItems;
  secondary?: AppearanceNavItems;
  navigationBar:
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Bottom;
        type: NavigationBarType.Fixed;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Start;
        type: NavigationBarType.Fixed | NavigationBarType.Floating;
        floatingOffset?: Pick<StyleObject, 'margin'>;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.Top;
        type: NavigationBarType.Fixed;
      })
    | (BaseNavigationBarAppearance & {
        position: NavigationPosition.End;
        type: NavigationBarType.Fixed;
      });
  navigationItem: NavigationItem;
  userReordered?: {
    primary: boolean;
    secondary: boolean;
  };
  styles?: Pick<
    StyleObject,
    | 'borderWidth'
    | 'borderRadius'
    | 'boxShadow'
    | 'height'
    | 'width'
    | 'padding'
    | 'borderColor'
    | 'gapSecondary'
  >;
  theme?: BlockThemeEnum;
};

export type Navigation = {
  // basePath for mounting client router, remove after testing if not needed
  basePath?: string;
  primary: NavItems;
  secondary?: NavItems;
  defaultPageId: string;
  appearance?: Partial<Record<NavigationResolution, NavigationAppearance>>;
};

export enum BorderVariants {
  FullySquare = 'FullySquare',
  SoftSquare = 'SoftSquare',
  Rounded = 'Rounded',
  FullyRounded = 'FullyRounded',
}

export enum LauncherPositionType {
  BOTTOM_START = 'BOTTOM_START',
  BOTTOM_END = 'BOTTOM_END',
  TOP_START = 'TOP_START',
  TOP_END = 'TOP_END',
  CUSTOM = 'CUSTOM',
}

export type LauncherPositionCoordinates = {
  bottom?: string | { custom: string };
  end?: string | { custom: string };
  top?: string | { custom: string };
  start?: string | { custom: string };
};

export type LauncherPosition = {
  type: LauncherPositionType;
  coordinates: LauncherPositionCoordinates;
};

export enum LauncherShape {
  CIRCLE = 'CIRCLE',
  SQUARE = 'SQUARE',
}

export type Launcher = {
  title: string;
  description: string;
  background: string;
  image?: string;
  iconColor?: string;
  shape: LauncherShape;
  position: LauncherPosition;
  gap: string | { custom: string };
  size: {
    width: string | { custom: string };
    height: string | { custom: string };
  };

  // optional message beside the logo
  message?: string;
  interactions?: {
    events?: Event[];
  };
};

export enum AppThemeMode {
  Light = 'light',
  Dark = 'dark',
}

type AppThemeOverrides = Record<string, string | undefined>;

export type AppThemeConfigType = AppThemeOverrides & {
  [AppThemeMode.Dark]?: Record<string, string | undefined>;
  border?: BorderVariants;
};

export enum IntegrationApps {
  CopilotChat = 'copilotChat',
}

export enum InterfaceType {
  Application = 'application',
  Module = 'module',
  Package = 'package',
}

export type CopilotChatIntegrationConfigType = {
  enabled: boolean;
  interfaceId: string;
  host: string;
  authToken: string;
  idpId: string;
};

export type Interface = {
  /**
   * The type of the component,
   * helps in identifying the use case of because we use the same entity to store multiple tenant's sitemap
   */
  // componentType: 'INTERFACE';
  name: string;
  parentInterfaceId?: string;
  standard: boolean;
  metadata: Metadata;
  locale?: {
    defaultLocale: string; // default locale of the interface
    supportedLocales?: string[]; // other supported locales of the interface
  };
  navigation: Navigation;
  deviceDetails?: DeviceDetails;
  manifest?: {
    type: ApplicationType;
    launcher?: Launcher;
  };
  interfacePageIdVsSlugMap?: Record<string, string | undefined>;
  theme?: {
    // stringified theme
    output?: string;
    color?: string;
    // Save theme variables in cssVars
    cssVars?: AppThemeConfigType;
    statusBar?: {
      style?: 'light' | 'dark' | 'auto';
    };
    /**
     * Default theme in which the interface should be loaded
     */
    defaultScheme?: 'light' | 'dark' | 'system';
    components?: ThemeComponentsType;
  };
  pagePermissions?: PagePermissions;
  customCode?: { header?: string; footer?: string };
  security?: SecurityProperties & {
    type: 'PUBLIC' | 'PRIVATE';
  };
  flags?: {
    shouldReEvaluateUsingEntityIds?: boolean;
    disabledMultipartUpload?: boolean;
    showBreadCrumbInIframe?: boolean;
    /**
     * Enable Automation Templates section in the UI
     */
    enableAutomationTemplates?: boolean;
  };
  integrations?: {
    [IntegrationApps.CopilotChat]: CopilotChatIntegrationConfigType;
  };
  type: InterfaceType;
  package?: {
    security?: SecurityProperties & {
      type: 'PUBLIC' | 'PRIVATE';
    };
  };
};

export type InterfaceEntity = Entity<Interface>;
